/**
 * E-Mail, Bürochat und Kalender.
 *
 * **Was hier steht, ist die Kommunikationsspur der Auftragsakte**, nicht ein
 * Mailprogramm. `07` §7.10 zieht die Grenze scharf: Das Büro arbeitet in
 * Bulwark, und was den Auftragsbezug hineinträgt, sind Plugins — nicht das
 * Portal, „weil sonst genau das Umschalten zwischen zwei Oberflächen
 * entstünde, das die Plattform vermeiden soll".
 *
 * Im Portal bleibt deshalb nur, was mehrere Quellen zusammenführt. Der
 * Ausschnitt je Nachricht ist absichtlich kurz: Ein Portal, das den Volltext
 * jeder Mail rendert, ist ein zweiter Mailclient — und der ist ausdrücklich
 * nicht gewollt.
 *
 * Anrufe und Videokonferenz fehlen, und zwar nach ADR-018 dauerhaft in
 * Version 1. Für Sprache bleibt der Telefonanruf über den `tel:`-Link.
 */

import type { Chatbeitrag, Chatraum, Nachricht, Termin } from "./modell.js";
import { AUFTRAG, CHATRAUM, DATEI, DOKUMENT, MAIL, PERSON, TERMIN } from "./kennungen.js";
import { ref } from "./modell.js";
import { werktag, zeitpunkt } from "./zeit.js";

const POSTFACH = "info@elektro-wagner-demo.de";

export function nachrichten(bezugstag: Date): readonly Nachricht[] {
  return [
    {
      ref: MAIL.anfrageSchule,
      von: "bauamt@schoenbrunn-demo.de",
      an: [POSTFACH],
      betreff: "Anfrage Elektroarbeiten Grundschule – Angebot erbeten",
      gesendet: zeitpunkt(bezugstag, 0, 7, 55),
      gelesen: false,
      richtung: "eingehend",
      ausschnitt:
        "für die Grundschule benötigen wir ein Angebot über die Erneuerung der "
        + "Beleuchtung in vier Klassenräumen. Ortstermin ist möglich …",
      anhaenge: [],
    },
    {
      ref: MAIL.rechnungElektrogrosshandel,
      von: "rechnung@elektrogrosshandel-nord-demo.de",
      an: [POSTFACH],
      betreff: "Rechnung 2026-114237 zu Ihrer Bestellung 88214",
      gesendet: zeitpunkt(bezugstag, 0, 6, 12),
      gelesen: false,
      richtung: "eingehend",
      ausschnitt: "anbei erhalten Sie unsere Rechnung als ZUGFeRD-Dokument …",
      anhaenge: [DOKUMENT.rechnungElektrogrosshandel],
    },
    {
      ref: MAIL.rechnungFehlerhaft,
      von: "office@leuchtenhandel-ost-demo.de",
      an: [POSTFACH],
      betreff: "Ihre Rechnung – ZUGFeRD",
      gesendet: zeitpunkt(bezugstag, -1, 9, 5),
      gelesen: true,
      richtung: "eingehend",
      ausschnitt: "im Anhang die Rechnung zu den Hallenstrahlern …",
      anhaenge: [DOKUMENT.rechnungFehlerhaft],
    },
    {
      ref: MAIL.rechnungKabelwerk,
      von: "buchhaltung@kabelwerk-sued-demo.de",
      an: [POSTFACH],
      betreff: "XRechnung 4471-2026",
      gesendet: zeitpunkt(bezugstag, -1, 14, 38),
      gelesen: true,
      richtung: "eingehend",
      ausschnitt: "Rechnung im Format XRechnung 3.0 …",
      anhaenge: [DOKUMENT.rechnungKabelwerk],
    },
    {
      ref: MAIL.reklamationBaeckerei,
      von: "b.hufnagel@baeckerei-hufnagel-demo.de",
      an: [POSTFACH],
      betreff: "Kühlung schaltet wieder ab",
      gesendet: zeitpunkt(bezugstag, -3, 5, 41),
      gelesen: true,
      richtung: "eingehend",
      ausschnitt:
        "heute Nacht ist die Kühlung wieder ausgegangen, Display E9. Die Ware konnten "
        + "wir noch retten, aber so geht das nicht weiter …",
      anhaenge: [],
    },
    {
      // Eine ausgehende Nachricht mit MagicLink statt Anhang — `04` §4.10
      // stellt beide Wege nebeneinander, und der Unterschied gehört im Portal
      // sichtbar zu sein.
      ref: MAIL.terminbestaetigungPraxis,
      von: POSTFACH,
      an: ["verwaltung@dr-hebling-demo.de"],
      betreff: "Terminbestätigung Aufmaß Behandlungsraum 3",
      gesendet: zeitpunkt(bezugstag, -2, 11, 4),
      gelesen: true,
      richtung: "ausgehend",
      ausschnitt:
        "wie besprochen kommen wir heute um 15:00 Uhr zum Aufmaß. Den Grundriss finden "
        + "Sie unter dem beigefügten Link …",
      anhaenge: [],
    },
    {
      ref: MAIL.angebotWallbox,
      von: POSTFACH,
      an: ["m.brandt-demo@posteo.de"],
      betreff: "Angebot Wallbox 11 kW – Auftrag 2490",
      gesendet: zeitpunkt(bezugstag, -14, 16, 30),
      gelesen: true,
      richtung: "ausgehend",
      ausschnitt: "vielen Dank für Ihre Anfrage. Anbei unser Angebot …",
      anhaenge: [DOKUMENT.abnahmeprotokollWallbox],
    },
    {
      ref: MAIL.rueckfrageHausverwaltung,
      von: "zeiler@zeiler-partner-demo.de",
      an: [POSTFACH],
      betreff: "Re: Zählerschrank Obere Sandstraße",
      gesendet: zeitpunkt(bezugstag, -3, 9, 12),
      gelesen: false,
      richtung: "eingehend",
      ausschnitt:
        "können Sie den Zählerschrank bitte zuerst begutachten, bevor wir die "
        + "Eigentümerversammlung damit befassen? …",
      anhaenge: [],
    },
    {
      ref: MAIL.lieferavisGrosshandel,
      von: "versand@elektrogrosshandel-nord-demo.de",
      an: [POSTFACH],
      betreff: "Lieferavis – Anlieferung morgen zwischen 7 und 9 Uhr",
      gesendet: zeitpunkt(bezugstag, -1, 17, 2),
      gelesen: false,
      richtung: "eingehend",
      ausschnitt: "Ihre Bestellung 88377 wird morgen früh angeliefert …",
      anhaenge: [],
    },
    {
      ref: MAIL.angebotsanfrageMetallbau,
      von: "info@metallbau-grasser-demo.de",
      an: [POSTFACH],
      betreff: "Zweite Halle – grobe Kostenschätzung?",
      gesendet: zeitpunkt(bezugstag, -8, 10, 15),
      gelesen: true,
      richtung: "eingehend",
      ausschnitt: "wir überlegen, die zweite Halle nächstes Jahr auch umzurüsten …",
      anhaenge: [],
    },
    {
      ref: MAIL.wartungsvertragEntwurf,
      von: POSTFACH,
      an: ["leitung@st-elisabeth-demo.de"],
      betreff: "Wartungsvertrag Rufanlage – Entwurf zur Durchsicht",
      gesendet: zeitpunkt(bezugstag, -30, 14, 20),
      gelesen: true,
      richtung: "ausgehend",
      ausschnitt: "anbei der Entwurf mit halbjährlichem Prüfintervall …",
      anhaenge: [DOKUMENT.wartungsvertragSeniorenheim],
    },
    {
      // Die Nachricht in Quarantäne. Der Ausschnitt sagt, dass etwas gesperrt
      // ist — er zeigt nicht, was drinstand. Genau hier wäre eine
      // hilfsbereite Vorschau der Fehler.
      ref: MAIL.verdaechtig,
      von: "mahnung@inkasso-zentrale-demo.net",
      an: [POSTFACH],
      betreff: "Offene Forderung – Vorgang 41822",
      gesendet: zeitpunkt(bezugstag, -2, 3, 17),
      gelesen: false,
      richtung: "eingehend",
      ausschnitt: "Anhang bei der Eingangsprüfung beanstandet – Inhalt gesperrt.",
      anhaenge: [],
    },
  ];
}

/**
 * Talk-Räume je Auftrag — `07` §7.10.
 *
 * Nicht jeder Auftrag hat einen: Der Satz lautet „jeder Auftrag **kann** einen
 * Nextcloud-Talk-Raum besitzen". Ein Bestand, in dem jeder Auftrag einen Raum
 * hat, zeigt nie, wie die Akte ohne Chat aussieht — und das ist der häufigere
 * Fall.
 */
export function chatraeume(): readonly Chatraum[] {
  return [
    {
      ref: CHATRAUM.kuehlung,
      auftrag: AUFTRAG.kuehlung,
      name: "2481 Bäckerei Hufnagel – Kühlung",
      teilnehmer: [PERSON.buero, PERSON.monteurA, PERSON.inhaber],
    },
    {
      ref: CHATRAUM.stoerung,
      auftrag: AUFTRAG.stoerung,
      name: "2505 Notdienst Verkaufsraum",
      teilnehmer: [PERSON.buero, PERSON.monteurA],
    },
    {
      ref: CHATRAUM.wallbox,
      auftrag: AUFTRAG.wallbox,
      name: "2490 Brandt – Wallbox",
      teilnehmer: [PERSON.buero, PERSON.monteurA],
    },
  ];
}

export function chatbeitraege(bezugstag: Date): readonly Chatbeitrag[] {
  const b = (
    id: string,
    raum: Chatbeitrag["raum"],
    autor: Chatbeitrag["autor"],
    tageVersatz: number,
    stunde: number,
    minute: number,
    text: string,
    anhang?: Chatbeitrag["anhang"],
  ): Chatbeitrag => ({
    ref: ref("nextcloud", "talk-message", id),
    raum,
    autor,
    gesendet: zeitpunkt(bezugstag, tageVersatz, stunde, minute),
    text,
    ...(anhang ? { anhang } : {}),
  });

  return [
    b("tm-4001", CHATRAUM.kuehlung, PERSON.buero, -7, 7, 30,
      "Marek, Hufnagel meldet E9 wieder. Kannst du vorbei?"),
    b("tm-4002", CHATRAUM.kuehlung, PERSON.monteurA, -7, 10, 45,
      "Fühler war verschmutzt, gereinigt und getauscht. Läuft wieder. "
      + "Wenn es nochmal kommt, ist es die Platine."),
    b("tm-4003", CHATRAUM.kuehlung, PERSON.buero, -3, 6, 10,
      "Es kam nochmal. Ich bestelle die Platine, du fährst nächste Woche."),
    b("tm-4004", CHATRAUM.kuehlung, PERSON.monteurA, 0, 8, 20,
      "Bin da. Platine ist ausgebaut, Typenschild passt zur bestellten.",
      DATEI.fotoKuehlungNachher),
    b("tm-4005", CHATRAUM.kuehlung, PERSON.buero, 0, 8, 34,
      "Gut. Parameter bitte vorher auslesen, steht auch im Wissen unter KR-40."),
    b("tm-4006", CHATRAUM.stoerung, PERSON.buero, 0, 6, 42,
      "Notdienst: Hufnagel Verkaufsraum ohne Strom. Du bist am nächsten dran."),
    b("tm-4007", CHATRAUM.stoerung, PERSON.monteurA, 0, 7, 49,
      "LS-Schalter war durch. Getauscht, läuft. Sprachnotiz ist dran.",
      DATEI.sprachnotizStoerung),
    b("tm-4008", CHATRAUM.wallbox, PERSON.buero, -1, 15, 0,
      "Material für Brandt ist da. Zuleitung 5x6, FI Typ B liegt bereit."),
    b("tm-4009", CHATRAUM.wallbox, PERSON.monteurA, -1, 15, 12,
      "Alles klar, ich mache morgen die Zuleitung und übermorgen die Box."),
  ];
}

export function termine(bezugstag: Date): readonly Termin[] {
  return [
    {
      ref: TERMIN.teamrunde,
      titel: "Wochenbesprechung",
      von: zeitpunkt(bezugstag, werktag(bezugstag, 1), 7, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 1), 7, 30),
      ort: "Büro Hirschaid",
      teilnehmer: [PERSON.inhaber, PERSON.buero, PERSON.monteurA, PERSON.monteurB],
    },
    {
      ref: TERMIN.praxisBesprechung,
      titel: "Aufmaß Praxis Dr. Hebling",
      von: zeitpunkt(bezugstag, 0, 15, 0),
      bis: zeitpunkt(bezugstag, 0, 16, 0),
      ort: "Hainstraße 12, 96047 Bamberg",
      teilnehmer: [PERSON.buero],
      auftrag: AUFTRAG.praxisumbau,
    },
    {
      ref: TERMIN.abnahmeWallbox,
      titel: "Abnahme Wallbox Brandt",
      von: zeitpunkt(bezugstag, werktag(bezugstag, 2), 16, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 2), 16, 30),
      ort: "Am Steigerwaldblick 9, 96138 Burgebrach",
      teilnehmer: [PERSON.inhaber, PERSON.monteurA],
      auftrag: AUFTRAG.wallbox,
    },
    {
      // Der Termin mit der Steuerberaterin — die einzige `external-partner`
      // im Bestand. Er steht hier, damit die Rolle nicht nur als Zeile in
      // einer Rechtetabelle existiert.
      ref: TERMIN.steuerberaterin,
      titel: "Vorbereitung Jahresabschluss – Dr. Ostermann",
      von: zeitpunkt(bezugstag, werktag(bezugstag, 5), 10, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 5), 12, 0),
      ort: "Büro Hirschaid",
      teilnehmer: [PERSON.inhaber, PERSON.buero, PERSON.partner],
    },
  ];
}
